import { Box, CircularProgress, useTheme } from "@mui/material";

export default function Loading(props) {
  const { color, margin, opaque, styles = {}, size = "medium", text = "Loading...", variant = "default" } = props;
  
  const theme = useTheme();
  const { palette = {} } = theme;
  const { success = {}, primary = {}, mode } = palette;
  const isDark = mode === "dark";
  
  // Brand colors
  const greenColor = success?.main || "#A0C46D";
  const navyColor = primary?.main || "#002843";
  
  // Theme-aware overlay backgrounds
  const overlayBg = isDark ? "rgba(0, 20, 31, 0.95)" : "rgba(255, 255, 255, 0.92)";
  const spinnerContainerBg = isDark ? "rgba(0, 30, 51, 0.98)" : "rgba(255, 255, 255, 0.98)";
  const spinnerContainerShadow = isDark 
    ? "0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05)" 
    : "0 8px 32px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.02)";
  const textColor = isDark ? "#94a3b8" : "#64748b";
  const trackColor = isDark ? "rgba(255, 255, 255, 0.1)" : "#e2e8f0";

  // Size configurations
  const sizes = {
    small: { spinner: 28, fontSize: "0.75rem", containerPadding: "16px" },
    medium: { spinner: 44, fontSize: "0.875rem", containerPadding: "24px" },
    large: { spinner: 64, fontSize: "1rem", containerPadding: "32px" },
  };

  const currentSize = sizes[size] || sizes.medium;

  // Pulse Animation Variant
  if (variant === "pulse") {
    return (
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
          height: "100%",
          justifyContent: "center",
          margin: margin,
          position: "absolute",
          width: "100%",
          zIndex: 1,
          ...(opaque ? {
            backgroundColor: overlayBg,
            backdropFilter: "blur(8px)",
            bottom: 0,
            left: 0,
            position: "fixed",
            right: 0,
            top: 0,
          } : {}),
          ...styles,
        }}
      >
        <Box sx={{ display: "flex", gap: "8px" }}>
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: "14px",
                height: "14px",
                borderRadius: "50%",
                backgroundColor: greenColor,
                animation: "pulseLoader 1.4s ease-in-out infinite",
                animationDelay: `${i * 0.2}s`,
                "@keyframes pulseLoader": {
                  "0%, 100%": { transform: "scale(0.6)", opacity: 0.4 },
                  "50%": { transform: "scale(1)", opacity: 1 },
                },
              }}
            />
          ))}
        </Box>
        {text && (
          <Box
            sx={{
              color: textColor,
              fontSize: currentSize.fontSize,
              fontWeight: 500,
              fontFamily: '"Lato", sans-serif',
              letterSpacing: "0.02em",
            }}
          >
            {text}
          </Box>
        )}
      </Box>
    );
  }

  // Logo Variant
  if (variant === "logo") {
    return (
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          flexDirection: "column",
          gap: "24px",
          height: "100%",
          justifyContent: "center",
          margin: margin,
          position: "absolute",
          width: "100%",
          zIndex: 1,
          ...(opaque ? {
            backgroundColor: overlayBg,
            backdropFilter: "blur(8px)",
            bottom: 0,
            left: 0,
            position: "fixed",
            right: 0,
            top: 0,
          } : {}),
          ...styles,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "80px",
            height: "80px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Rotating ring */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              border: "3px solid transparent",
              borderTopColor: greenColor,
              borderRadius: "50%",
              animation: "spinLoader 1s linear infinite",
              "@keyframes spinLoader": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
          />
          {/* Inner glow */}
          <Box
            sx={{
              width: "60px",
              height: "60px",
              borderRadius: "50%",
              background: `linear-gradient(135deg, ${greenColor}15 0%, ${navyColor}10 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "pulseGlow 2s ease-in-out infinite",
              "@keyframes pulseGlow": {
                "0%, 100%": { boxShadow: `0 0 20px ${greenColor}30` },
                "50%": { boxShadow: `0 0 40px ${greenColor}50` },
              },
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 800,
                color: isDark ? "#ffffff" : navyColor,
                fontFamily: '"Lato", sans-serif',
              }}
            >
              LX
            </Box>
          </Box>
        </Box>
        {text && (
          <Box
            sx={{
              color: textColor,
              fontSize: currentSize.fontSize,
              fontWeight: 500,
              fontFamily: '"Lato", sans-serif',
              letterSpacing: "0.02em",
            }}
          >
            {text}
          </Box>
        )}
      </Box>
    );
  }

  // Default spinner variant
  return (
    <Box
      sx={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        height: "100%",
        justifyContent: "center",
        margin: margin,
        position: "absolute",
        visibility: "visible",
        width: "100%",
        zIndex: 1,
        ...(opaque ? {
          backgroundColor: overlayBg,
          backdropFilter: "blur(8px)",
          bottom: 0,
          left: 0,
          position: "fixed",
          right: 0,
          top: 0,
        } : {}),
        ...styles,
      }}
    >
      {/* Spinner container with premium styling */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: currentSize.containerPadding,
          borderRadius: "20px",
          backgroundColor: spinnerContainerBg,
          boxShadow: spinnerContainerShadow,
          animation: "containerFadeIn 0.3s ease-out",
          "@keyframes containerFadeIn": {
            from: { opacity: 0, transform: "scale(0.95)" },
            to: { opacity: 1, transform: "scale(1)" },
          },
        }}
      >
        <Box sx={{ position: "relative", display: "inline-flex" }}>
          {/* Background track */}
          <CircularProgress
            variant="determinate"
            value={100}
            size={currentSize.spinner}
            thickness={4}
            sx={{
              color: trackColor,
              position: "absolute",
            }}
          />
          {/* Secondary spinner (slower, navy) */}
          <CircularProgress
            variant="determinate"
            size={currentSize.spinner}
            thickness={4}
            sx={{
              color: navyColor,
              position: "absolute",
              opacity: 0.3,
              animation: "spinSlow 3s linear infinite",
              "@keyframes spinSlow": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
              },
            }}
            value={30}
          />
          {/* Primary animated spinner */}
          <CircularProgress
            size={currentSize.spinner}
            thickness={4}
            sx={{
              color: color || greenColor,
              animationDuration: "1s",
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
        </Box>
      </Box>
      
      {/* Loading text with subtle animation */}
      {text && (
        <Box
          sx={{
            color: textColor,
            fontSize: currentSize.fontSize,
            fontWeight: 500,
            fontFamily: '"Lato", sans-serif',
            letterSpacing: "0.02em",
            animation: "textFadeIn 0.4s ease-out 0.1s both",
            "@keyframes textFadeIn": {
              from: { opacity: 0, transform: "translateY(8px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {text}
        </Box>
      )}
    </Box>
  );
}
